import { Component, OnInit, AfterViewInit, ViewChild, TemplateRef, Output, EventEmitter, HostListener, ChangeDetectorRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig, ModalComponent, DateRangePickerComponent, DateRange } from '../primeng';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { FormatService } from '../../services/format.service';
import { AclApdApiService } from '@angular-workspace/services';

export interface AssetRequestData {
  assetName: string;
  assetType: string;
  requestedBy: string;
  requestType: string;
  organisationName: string;
  purpose: string;
  requestDate: string;
  requestedAs: string;
  requestStatus: string;
  action?: string;
  id?: string;
  originalData?: any;
}

interface UserDetailEntry {
  label: string;
  value: string;
}

@Component({
  selector: 'app-asset-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    ModalComponent,
    DateRangePickerComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './asset-request.component.html',
  styleUrl: './asset-request.component.scss',
})
export class AssetRequestComponent implements OnInit, AfterViewInit {
  @Input() showActionColumn = true;
  @Input() showPurposeColumn = true;
  @Input() showRequestTypeColumn = true;
  @Input() hideHorizontalScroll = false;
  /** When true, table sort triggers `reloadData` and `getSortQueryString()` for server-side ordering (platform admin). */
  @Input() serverSideSorting = false;
  @ViewChild('expandCell', { static: false }) expandCellTemplate!: TemplateRef<any>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('statusCell', { static: false }) statusCellTemplate!: TemplateRef<any>;
  @ViewChild('purposeCell', { static: false }) purposeCellTemplate!: TemplateRef<any>;
  @Output() reloadData = new EventEmitter<void>();

  openDropdownId: string | null = null;
  expandedRows: Set<string> = new Set();
  showPurposeModal = false;
  purposeModalText = '';

  // Approve modal state
  isApproveModalVisible = false;
  selectedRequest: AssetRequestData | null = null;
  expirationDate: DateRange | null = null;
  minDate: Date = new Date();

  constructor(
    private formatService: FormatService,
    private aclApdApiService: AclApdApiService,
    private cdr: ChangeDetectorRef
  ) {}

  columns: TableColumn[] = [
    {
      key: 'expand',
      label: '',
      sortable: false,
      width: '44px',
      fixed: 'left',
    },
    {
      key: 'assetName',
      label: 'Asset Name',
      sortable: true,
      width: '20%'
    },
    {
      key: 'assetType',
      label: 'Asset Type',
      sortable: true,
      width: '12%'
    },
    {
      key: 'requestedBy',
      label: 'Requested By',
      sortable: true,
      width: '15%'
    },
    {
      key: 'requestType',
      label: 'Request Type',
      sortable: true,
      width: '12%'
    },
    {
      key: 'purpose',
      label: 'Purpose',
      sortable: false,
      width: '20%'
    },
    {
      key: 'requestDate',
      label: 'Requested At',
      sortable: true,
      width: '12%',
      align: 'left'
    },
    {
      key: 'requestedAs',
      label: 'Requested As',
      sortable: true,
      width: '12%'
    },
    {
      key: 'requestStatus',
      label: 'Request Status',
      sortable: true,
      width: '10%'
    },
    {
      key: 'action',
      label: 'Action',
      sortable: false,
      width: '8%',
      align: 'left',
      fixed: 'right'
    }
  ];


  /** True when Accept/Reject should be disabled (status is Granted or Rejected). */
  isApproveRejectDisabled(row: AssetRequestData): boolean {
    const s = (row?.requestStatus ?? '').toString().toLowerCase();
    return s === 'granted' || s === 'approved' || s === 'rejected';
  }

  /** Returns chip variant class for Request Status (pill styling). */
  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip--default';
    const s = status.toLowerCase();
    if (s === 'granted' || s === 'approved') return 'status-chip status-chip--granted';
    if (s === 'pending') return 'status-chip status-chip--pending';
    if (s === 'rejected') return 'status-chip status-chip--rejected';
    return 'status-chip status-chip--default';
  }

  // Original data (unfiltered) - will be populated from API
  allAssetRequestsData: AssetRequestData[] = [];

  // Asset requests data for current page - will be populated from API
  assetRequestsData: AssetRequestData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };

  loading = false;
  emptyMessage = 'No asset requests found';

  ngOnInit(): void {
    if (!this.showActionColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'action');
    }
    if (!this.showPurposeColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'purpose');
    }
    if (!this.showRequestTypeColumn) {
      this.columns = this.columns.filter((column) => column.key !== 'requestType');
    }
    // Data will be loaded from parent component via mapProviderAssetRequestsResponse()
    // Don't call loadAssetRequests() here
  }

  ngAfterViewInit(): void {
    // Assign cell templates synchronously to avoid race condition with data loading
    this.assignCellTemplates();
    this.cdr.detectChanges();
    
    // Add scroll listener to table wrapper for horizontal scroll
    const tableWrapper = document.querySelector('.table-wrapper');
    if (tableWrapper) {
      tableWrapper.addEventListener('scroll', () => {
        if (this.openDropdownId !== null) {
          this.updateDropdownPosition();
        }
      });
    }
  }

  private assignCellTemplates(): void {
    const expandColumn = this.columns.find(col => col.key === 'expand');
    if (expandColumn && this.expandCellTemplate) {
      expandColumn.cellTemplate = this.expandCellTemplate;
    }
    const purposeColumn = this.columns.find(col => col.key === 'purpose');
    if (purposeColumn && this.purposeCellTemplate) {
      purposeColumn.cellTemplate = this.purposeCellTemplate;
    }
    const statusColumn = this.columns.find(col => col.key === 'requestStatus');
    if (statusColumn && this.statusCellTemplate) {
      statusColumn.cellTemplate = this.statusCellTemplate;
    }
    const actionColumn = this.columns.find(col => col.key === 'action');
    if (actionColumn && this.actionCellTemplate) {
      actionColumn.cellTemplate = this.actionCellTemplate;
    }
  }

  openPurposeModal(purpose: string): void {
    this.purposeModalText = purpose || '—';
    this.showPurposeModal = true;
    this.cdr.detectChanges();
  }

  getUserDetailEntriesForRow(row: AssetRequestData): UserDetailEntry[] {
    const userObject = row.originalData?.user;
    if (!userObject || typeof userObject !== 'object') {
      return [{ label: 'Details', value: 'User details are not available for this request.' }];
    }
    return this.buildUserDetailEntries(userObject);
  }

  private buildUserDetailEntries(userObject: Record<string, any>): UserDetailEntry[] {
    const firstName = (
      userObject['consumerFirstName'] ||
      userObject['consumer_first_name'] ||
      userObject['firstName'] ||
      userObject['first_name'] ||
      ''
    ).toString().trim();

    const lastName = (
      userObject['consumerLastName'] ||
      userObject['consumer_last_name'] ||
      userObject['lastName'] ||
      userObject['last_name'] ||
      ''
    ).toString().trim();

    const combinedName = [firstName, lastName].filter(Boolean).join(' ').trim();

    const details = Object.entries(userObject)
      .filter(([key, value]) => {
        const normalizedKey = key.toLowerCase().replace(/_/g, '');
        if (
          normalizedKey === 'consumerid' ||
          normalizedKey === 'consumerfirstname' ||
          normalizedKey === 'consumerlastname' ||
          normalizedKey === 'firstname' ||
          normalizedKey === 'lastname'
        ) {
          return false;
        }
        return value !== undefined && value !== null && String(value).trim() !== '';
      })
      .map(([key, value]) => ({
        label: this.formatUserDetailLabel(key),
        value: String(value),
      }));

    if (combinedName) {
      details.unshift({ label: 'Consumer Name', value: combinedName });
    }

    return details;
  }

  private formatUserDetailLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  private getRequestedByDisplay(item: any): string {
    const firstName = (
      item.user?.consumerFirstName ||
      item.user?.consumer_first_name ||
      item.user?.firstName ||
      item.user?.first_name ||
      ''
    ).toString().trim();

    const lastName = (
      item.user?.consumerLastName ||
      item.user?.consumer_last_name ||
      item.user?.lastName ||
      item.user?.last_name ||
      ''
    ).toString().trim();

    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || item.user?.consumerEmail || item.userName || item.user_name || item.requestedBy || item.requested_by || 'N/A';
  }

  /**
   * Map provider asset requests API response to AssetRequestData format
   * @param response API response from providerAssetRequests()
   */
  mapProviderAssetRequestsResponse(response: any): void {
    this.loading = true;
    try {
      let mappedData: AssetRequestData[] = [];

      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => {
          // Format request date
          const requestDate = item.createdAt || item.updatedAt || item.requestedAt || item.requestCreatedAt || '';

          // Requested As (role/entity request is being made as)
          const requestedAs = this.formatService.toPascalCase(
            item.requestAs ||
            item.request_as ||
            item.requestedAs ||
            item.requested_as ||
            item.additionalInfo?.requestAs ||
            item.additionalInfo?.request_as ||
            item.additionalInfo?.requestedAs ||
            item.additionalInfo?.requested_as ||
            item.user?.requestedAs ||
            item.user?.requested_as ||
            item.role ||
            item.accessType ||
            ''
          );
          
          // Format request type to PascalCase
          const requestType = this.formatService.toPascalCase(item.requestType || item.accessType || '');
          
          // Format asset type
          let assetType = 'N/A';
          if (item.asset?.assetType || item.assetType) {
            assetType = this.formatService.formatAssetType(item.asset?.assetType || item.assetType);
          }
          
          // Format request status to PascalCase
          const requestStatus = this.formatService.toPascalCase(
            item.requestStatus || item.request_status || item.status || 'N/A'
          );

          return {
            assetName: item.asset?.assetName || item.assetName || item.name || 'N/A',
            assetType: assetType,
            requestedBy: this.getRequestedByDisplay(item),
            requestType: requestType,
            organisationName: item.organisationName || item.organisation_name || item.orgName || item.org_name || 'N/A',
            purpose: item.additionalInfo?.purposeOfRequest || item.additionalInfo?.purpose_of_request || item.purpose || 'N/A',
            requestDate: requestDate ? this.formatDate(requestDate) : 'N/A',
            requestedAs: requestedAs || 'N/A',
            requestStatus: requestStatus,
            action: 'Action',
            originalData: item // Store original API response data
          };
        });

        // Update pagination from paginationInfo
        const totalCount = response['paginationInfo']?.totalCount ?? mappedData.length;
        this.pagination = {
          ...this.pagination,
          totalItems: totalCount
        };
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result array
        const totalCount = response['paginationInfo']['totalCount'] ?? 0;
        this.pagination = {
          ...this.pagination,
          totalItems: totalCount
        };
        mappedData = [];
      } else if (Array.isArray(response)) {
        // Fallback: Handle direct array response
        mappedData = response.map((item: any) => {
          const requestDate = item.createdAt || item.updatedAt || item.requestedAt || item.requestCreatedAt || '';
          const requestedAs = this.formatService.toPascalCase(
            item.requestAs ||
            item.request_as ||
            item.requestedAs ||
            item.requested_as ||
            item.additionalInfo?.requestAs ||
            item.additionalInfo?.request_as ||
            item.additionalInfo?.requestedAs ||
            item.additionalInfo?.requested_as ||
            item.user?.requestedAs ||
            item.user?.requested_as ||
            item.role ||
            item.accessType ||
            ''
          );
          const requestType = this.formatService.toPascalCase(item.requestType || item.accessType || '');
          let assetType = 'N/A';
          if (item.asset?.assetType || item.assetType) {
            assetType = this.formatService.formatAssetType(item.asset?.assetType || item.assetType);
          }
          const requestStatus = this.formatService.toPascalCase(
            item.requestStatus || item.request_status || item.status || 'N/A'
          );

          return {
            assetName: item.asset?.assetName || item.assetName || item.name || 'N/A',
            assetType: assetType,
            requestedBy: this.getRequestedByDisplay(item),
            requestType: requestType,
            organisationName: item.organisationName || item.organisation_name || item.orgName || item.org_name || 'N/A',
            purpose: item.additionalInfo?.purposeOfRequest || item.additionalInfo?.purpose_of_request || item.purpose || 'N/A',
            requestDate: requestDate ? this.formatDate(requestDate) : 'N/A',
            requestedAs: requestedAs || 'N/A',
            requestStatus: requestStatus,
            action: 'Action',
            originalData: item
          };
        });
        this.pagination = {
          ...this.pagination,
          totalItems: mappedData.length
        };
      }

      this.assetRequestsData = mappedData;
      this.allAssetRequestsData = [...mappedData];
      // Ensure templates are assigned and trigger change detection
      this.assignCellTemplates();
      this.cdr.detectChanges();
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No asset requests found';
      }
    } catch (error) {
      console.error('Error mapping provider asset requests response:', error);
      this.assetRequestsData = [];
      this.allAssetRequestsData = [];
      this.pagination = {
        ...this.pagination,
        totalItems: 0
      };
      this.emptyMessage = 'No asset requests found';
    } finally {
      this.loading = false;
    }
  }

  /** Format as DD-MMM-YYYY HH:MM for Requested At column */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const day = date.getDate().toString().padStart(2, '0');
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}-${month}-${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  }

  onPageChange(page: number): void {
    this.pagination = {
      ...this.pagination,
      currentPage: page
    };
    // Emit event to parent to reload data with new page
    this.reloadData.emit();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = {
      ...this.pagination,
      pageSize: pageSize,
      currentPage: 1
    };
    // Emit event to parent to reload data with new page size
    this.reloadData.emit();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    // Sort the data based on column and direction
    this.assetRequestsData.sort((a, b) => {
      const aValue = a[event.column as keyof AssetRequestData] || '';
      const bValue = b[event.column as keyof AssetRequestData] || '';
      
      if (event.direction === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }

  toggleActionDropdown(event: Event, row: AssetRequestData): void {
    event.stopPropagation();
    const rowId = row.originalData?.id || row.id || JSON.stringify(row);
    const wasOpen = this.openDropdownId === rowId;
    this.openDropdownId = wasOpen ? null : rowId;
    
    // Position dropdown using fixed positioning to escape scroll container
    if (!wasOpen && this.openDropdownId === rowId) {
      setTimeout(() => {
        const button = (event.target as HTMLElement).closest('.action-btn') as HTMLElement;
        if (!button) {
          console.error('Action button not found');
          return;
        }
        
        // Try multiple selectors to find the dropdown
        const dropdownId = row.originalData?.id || row.id || '';
        let dropdown = document.querySelector(`[data-dropdown-id="${dropdownId}"]`) as HTMLElement;
        
        // If not found, try finding it by parent element
        if (!dropdown && button) {
          dropdown = button.parentElement?.querySelector('.action-dropdown-menu') as HTMLElement;
        }
        
        // If still not found, try finding by closest action-cell-wrapper
        if (!dropdown && button) {
          const wrapper = button.closest('.action-cell-wrapper');
          dropdown = wrapper?.querySelector('.action-dropdown-menu') as HTMLElement;
        }
        
        if (!dropdown) {
          console.error('Dropdown not found. RowId:', rowId, 'DropdownId:', dropdownId);
          return;
        }
        
        const rect = button.getBoundingClientRect();
        console.log('Positioning dropdown:', { 
          top: rect.bottom + 4, 
          left: rect.left, 
          width: rect.width,
          buttonRect: rect 
        });
        
        dropdown.style.position = 'fixed';
        dropdown.style.top = `${rect.bottom + 4}px`;
        dropdown.style.left = `${rect.left}px`;
        dropdown.style.width = `${rect.width}px`;
        dropdown.style.minWidth = `${rect.width}px`;
        dropdown.style.display = 'block';
        dropdown.style.visibility = 'visible';
        dropdown.style.opacity = '1';
        dropdown.style.zIndex = '10000';
      }, 0);
    }
  }

  toggleRowExpansion(row: AssetRequestData): void {
    const rowId = row.originalData?.id || row.id || JSON.stringify(row);
    if (this.expandedRows.has(rowId)) {
      this.expandedRows.delete(rowId);
    } else {
      this.expandedRows.add(rowId);
    }
  }

  isRowExpanded(row: AssetRequestData): boolean {
    const rowId = row.originalData?.id || row.id || JSON.stringify(row);
    return this.expandedRows.has(rowId);
  }

  getIsRowExpanded(): (row: AssetRequestData) => boolean {
    return (row: AssetRequestData) => this.isRowExpanded(row);
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
  }

  isDropdownOpen(row: AssetRequestData): boolean {
    const rowId = row.originalData?.id || row.id || JSON.stringify(row);
    const isOpen = this.openDropdownId === rowId;
    return isOpen;
  }

  async handleAction(action: string, row: AssetRequestData): Promise<void> {
    this.closeActionDropdown();

    if (action === 'accept') {
      // Open approve modal to capture expiry before approving
      this.selectedRequest = row;
      this.expirationDate = null;
      // Defer modal open to next tick so dropdown close doesn't interfere with click
      setTimeout(() => {
        this.isApproveModalVisible = true;
        this.cdr.detectChanges();
      }, 0);
      return;
    }

    const requestId = row.originalData?.id || row.originalData?.requestId;
    if (!requestId) {
      console.error('Request ID not found');
      return;
    }

    try {
      let payload: any = {};
      
      if (action === 'reject') {
        payload = { 
          requestId: requestId,
          status: 'rejected' 
        };
      } else {
        console.warn('Unknown action:', action);
        return;
      }

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      console.log(`Successfully ${action}ed access request:`, requestId);
      
      // Reload data to reflect the updated status
      this.reloadData.emit();
    } catch (error) {
      console.error(`Error ${action}ing access request:`, error);
      // Error toaster is handled by networkService interceptor
    }
  }

  /** Confirm handler for approve modal - sends status=granted with expiryAt */
  async onApproveConfirm(): Promise<void> {
    if (!this.selectedRequest) {
      return;
    }

    const requestId =
      this.selectedRequest.originalData?.id ||
      this.selectedRequest.originalData?.requestId;

    if (!requestId) {
      console.error('Request ID not found while approving');
      return;
    }

    if (!this.expirationDate?.startDate) {
      console.error('Expiry date is required');
      return;
    }

    const expiryAt = this.formatExpiryAt(this.expirationDate.startDate);

    try {
      const payload = {
        requestId: requestId,
        status: 'granted',
        expiryAt: expiryAt,
      };

      await this.aclApdApiService.updateAccessRequest(requestId, payload);
      console.log('Successfully approved access request with expiryAt:', {
        requestId,
        expiryAt,
      });

      this.closeApproveModal();
      this.reloadData.emit();
    } catch (error) {
      console.error('Error approving access request with expiryAt:', error);
      // Error toaster is handled by networkService interceptor
    }
  }

  closeApproveModal(): void {
    this.isApproveModalVisible = false;
    this.selectedRequest = null;
    this.expirationDate = null;
  }

  isExpiryValid(): boolean {
    if (!this.expirationDate?.startDate) return false;
    const d = new Date(this.expirationDate.startDate);
    d.setHours(23, 59, 59, 999);
    return d.getTime() > Date.now();
  }

  private formatExpiryAt(date: Date): string {
    const d = new Date(date);
    d.setHours(23, 59, 0, 0);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T23:59:00`;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    // Close dropdown when clicking outside
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null && !target.closest('.action-cell-wrapper')) {
      this.closeActionDropdown();
    }
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll(event: Event): void {
    // Update dropdown position on scroll
    if (this.openDropdownId !== null) {
      this.updateDropdownPosition();
    }
  }

  private updateDropdownPosition(): void {
    if (!this.openDropdownId) return;
    
    setTimeout(() => {
      // Try multiple selectors to find the dropdown
      const dropdownId = this.openDropdownId || '';
      let dropdown = document.querySelector(`[data-dropdown-id="${dropdownId}"]`) as HTMLElement;
      
      if (!dropdown) {
        // Try finding by action-cell-wrapper
        const wrapper = document.querySelector('.action-cell-wrapper');
        dropdown = wrapper?.querySelector('.action-dropdown-menu') as HTMLElement;
      }
      
      if (!dropdown) return;
      
      // Find the button that opened this dropdown
      const button = dropdown.closest('.action-cell-wrapper')?.querySelector('.action-btn') as HTMLElement;
      if (!button) return;
      
      const rect = button.getBoundingClientRect();
      dropdown.style.top = `${rect.bottom + 4}px`;
      dropdown.style.left = `${rect.left}px`;
    }, 0);
  }
}


